/*
  ====================================================================================================
  MERSALG + KARUSELLER (Claude 28.09.2026)
  ====================================================================================================
  Lastes med defer fra snippets/mm-cart-upsell-rules.liquid (mode: 'script'), som rendres i kurv-skuffen på
  alle sider. Flyttet hit fra inline-script så nettleseren mellomlagrer den (ingen ekstra HTML per side).

  1. DRA MED MUS (desktop) for tre karuseller, så de føles like:
       .mmx-upsell__list                – mersalg i kurven (snippets/mm-cart-upsell-rules.liquid)
       .product-info .mm-upsell__list   – mersalg på produktsiden (snippets/mm-pdp-upsell.liquid)
       .mm-kits__list                   – «Inngår også i disse kit» (snippets/mm-included-in-kits.liquid)
     Mobil bruker nettleserens egen sveip og berøres ikke.
     Ved slipp: raden «kastes» videre ut fra farten (MOMENTUM_MS) og glir til nærmeste kort der den ville
     stoppet. Uten fart: har man dratt mer enn SWITCH_RATIO av avstanden til neste kort, går den dit, ellers
     tilbake. Smale kort (kurven) glir dermed jevnt over flere kort; brede kort blar ett om gangen.
     Egen glidning (requestAnimationFrame, ease-out); snap og myk rulling er av mens den drar/glir (også
     temaets scroll-snap-stop på kits-kortene). Lenker/bilder kan ikke «dras ut»; klikket rett etter et dra
     sperres i 50 ms. Musehjulet overtas ikke.

  2. FREMDRIFTSLINJE for mersalget i kurven (.mmx-upsell__bar).

  3. «KJØP» ([data-mmx-add]) i kurven og på produktsiden: legger varen i kurven med _upsell,
     _upsell_source og _upsell_surface (cart | product), oppdaterer kurv-skuffen via Section Rendering og
     åpner den. På produktsiden viser knappen «✓» et øyeblikk.

  4. SPORING: mm_upsell_shown / _clicked / _added via Shopify.analytics.publish.
*/
(() => {
  if (window.__mmxUpsell) return;
  window.__mmxUpsell = true;

  const DRAG_LISTS = '.mmx-upsell__list, .product-info .mm-upsell__list, .mm-kits__list';
  const SWITCH_RATIO = 0.3; // andel av avstanden til neste kort man må dra (uten fart) for å bla
  const FLICK_SPEED = 0.35; // px/ms: under dette regnes slippet som «uten fart»
  const MOMENTUM_MS = 260; // hvor langt kastet går: fart (px/ms) × denne
  const MAX_CARDS_PER_FLICK = 6;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_ADD_URL = root + 'cart/add.js';
  const CART_URL = root + 'cart.js';

  const track = (name, payload) => {
    try {
      window.Shopify?.analytics?.publish?.(name, payload);
    } catch (error) {
      /* statistikk skal aldri stoppe et kjøp */
    }
  };

  /* ---------- Fremdriftslinje (kurven) ---------- */
  const updateBar = (list) => {
    const bar = list.closest('.mmx-upsell')?.querySelector('.mmx-upsell__bar');
    const thumb = bar?.firstElementChild;
    if (!thumb) return;
    const max = list.scrollWidth - list.clientWidth;
    if (list.clientWidth === 0 || max <= 1) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;
    const width = Math.max((list.clientWidth / list.scrollWidth) * 100, 12);
    const position = Math.min(Math.max(list.scrollLeft / max, 0), 1);
    thumb.style.width = width + '%';
    thumb.style.transform = 'translateX(' + position * ((100 - width) / width) * 100 + '%)';
  };

  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target instanceof Element && event.target.classList.contains('mmx-upsell__list')) {
        updateBar(event.target);
      }
    },
    true
  );

  const observed = new WeakSet();
  const resizeObserver =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver((entries) => entries.forEach((e) => updateBar(e.target))) : null;

  /* ---------- Dra med musen (desktop) ---------- */

  /* Scroll-posisjonene kortene legger seg på (stigende), inkl. listens scroll-padding. */
  const snapPoints = (list) => {
    const cs = getComputedStyle(list);
    const pad = parseFloat(cs.scrollPaddingLeft) || 0;
    const base = list.getBoundingClientRect().left + list.clientLeft + pad;
    const max = list.scrollWidth - list.clientWidth;
    const points = Array.from(list.children)
      .filter((cell) => cell.offsetWidth > 0)
      .map((cell) => Math.round(Math.min(Math.max(list.scrollLeft + cell.getBoundingClientRect().left - base, 0), max)));
    return [...new Set(points)].sort((a, b) => a - b);
  };

  const nearestIndex = (points, value) =>
    points.reduce((best, point, i) => (Math.abs(point - value) < Math.abs(points[best] - value) ? i : best), 0);

  /* Snap, myk rulling og kortenes snap-stop av mens raden dras eller glir, så de ikke kjemper mot oss. */
  const freeze = (list) => {
    list.style.scrollSnapType = 'none';
    list.style.scrollBehavior = 'auto';
  };

  const unfreeze = (list) => {
    list.style.scrollSnapType = '';
    list.style.scrollBehavior = '';
  };

  const stopAnimation = (list) => {
    if (list._mmxAnim) cancelAnimationFrame(list._mmxAnim);
    list._mmxAnim = 0;
  };

  const glideTo = (list, target) => {
    stopAnimation(list);
    const from = list.scrollLeft;
    const delta = target - from;
    if (Math.abs(delta) < 1) {
      list.scrollLeft = target;
      unfreeze(list);
      return;
    }
    const duration = Math.min(520, Math.max(240, Math.abs(delta) * 0.9));
    const startTime = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      list.scrollLeft = from + delta * ease(progress);
      if (progress < 1) {
        list._mmxAnim = requestAnimationFrame(step);
      } else {
        list._mmxAnim = 0;
        list.scrollLeft = target;
        unfreeze(list);
      }
    };
    list._mmxAnim = requestAnimationFrame(step);
  };

  let drag = null;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const list = event.target.closest?.(DRAG_LISTS);
    if (!list || list.scrollWidth - list.clientWidth <= 1) return;
    stopAnimation(list);
    drag = { list, startX: event.clientX, startLeft: list.scrollLeft, moved: false, samples: [{ t: performance.now(), x: event.clientX }] };
  });

  document.addEventListener(
    'dragstart',
    (event) => {
      if (event.target instanceof Element && event.target.closest(DRAG_LISTS)) event.preventDefault();
    },
    true
  );

  document.addEventListener('pointermove', (event) => {
    if (!drag) return;
    const dx = event.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(dx) <= 6) return;
      drag.moved = true;
      drag.list.classList.add('is-dragging');
      freeze(drag.list);
    }
    drag.list.scrollLeft = drag.startLeft - dx;
    const now = performance.now();
    drag.samples.push({ t: now, x: event.clientX });
    while (drag.samples.length > 2 && now - drag.samples[0].t > 80) drag.samples.shift();
    event.preventDefault();
  });

  const endDrag = () => {
    if (!drag) return;
    const { list, moved, startLeft, samples } = drag;
    drag = null;
    if (!moved) return;
    list.classList.remove('is-dragging');

    // Fart de siste ~80 ms (px/ms, positiv = rulle mot høyre). Står musen stille før slipp → 0.
    const first = samples[0];
    const last = samples[samples.length - 1];
    const dt = Math.max(last.t - first.t, 1);
    const velocity = performance.now() - last.t > 60 ? 0 : -(last.x - first.x) / dt;

    const points = snapPoints(list);
    if (points.length < 2) {
      glideTo(list, points[0] || 0);
    } else {
      const current = list.scrollLeft;
      const startIndex = nearestIndex(points, startLeft);
      let index;

      if (Math.abs(velocity) >= FLICK_SPEED) {
        // Kast: hvor ville raden stoppet? Legg den på nærmeste kort der – minst ett kort i kastretningen.
        const dir = Math.sign(velocity);
        index = nearestIndex(points, current + velocity * MOMENTUM_MS);
        if (dir > 0 && index <= startIndex) index = startIndex + 1;
        if (dir < 0 && index >= startIndex) index = startIndex - 1;
        index = Math.min(Math.max(index, startIndex - MAX_CARDS_PER_FLICK), startIndex + MAX_CARDS_PER_FLICK);
      } else {
        // Rolig dra: nærmeste kort, men bytter allerede ved SWITCH_RATIO av avstanden til neste kort.
        const distance = current - startLeft;
        index = nearestIndex(points, current);
        if (index === startIndex && distance !== 0) {
          const next = startIndex + Math.sign(distance);
          if (next >= 0 && next < points.length) {
            const gap = Math.abs(points[next] - points[startIndex]) || 1;
            if (Math.abs(distance) >= gap * SWITCH_RATIO) index = next;
          }
        }
      }

      index = Math.min(Math.max(index, 0), points.length - 1);
      glideTo(list, points[index]);
    }

    list.dataset.mmxDragged = '1';
    setTimeout(() => {
      delete list.dataset.mmxDragged;
    }, 50);
  };
  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);
  window.addEventListener('blur', endDrag);

  document.addEventListener(
    'click',
    (event) => {
      const list = event.target.closest?.(DRAG_LISTS);
      if (list && list.dataset.mmxDragged) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  /* ---------- Visning ---------- */
  const markSeen = (el) => {
    const buttons = Array.from(el.querySelectorAll('[data-mmx-add]'));
    const ids = buttons.map((button) => button.dataset.variantId).join(',');
    if (!ids || el.dataset.mmxSeen === ids) return;
    el.dataset.mmxSeen = ids;
    track('mm_upsell_shown', {
      surface: el.dataset.mmxSurface || 'cart',
      source: 'regler',
      variantIds: ids.split(','),
      sources: buttons.map((button) => button.dataset.upsellSource),
      count: buttons.length,
    });
  };

  const scan = () => {
    document.querySelectorAll('.mmx-upsell, .mmx-pdp-upsell').forEach(markSeen);
    document.querySelectorAll('.mmx-upsell__list').forEach((list) => {
      updateBar(list);
      if (resizeObserver && !observed.has(list)) {
        observed.add(list);
        resizeObserver.observe(list);
      }
    });
  };

  const openCartDrawer = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    for (const method of ['show', 'open', 'showDialog']) {
      if (typeof drawer[method] === 'function') {
        drawer[method]();
        return;
      }
    }
    drawer.setAttribute('open', '');
  };

  const cartSectionId = (button) => {
    const fromCart = button.closest('.mmx-upsell')?.dataset.sectionId;
    if (fromCart) return fromCart;
    const section = document.querySelector('[id^="shopify-section-"][id$="__cart-drawer"]');
    return section ? section.id.replace('shopify-section-', '') : undefined;
  };

  /* ---------- Kjøp ---------- */
  const addToCart = async (button) => {
    const surface = button.dataset.upsellSurface || 'cart';
    const label = button.textContent;
    button.setAttribute('disabled', '');
    button.setAttribute('aria-busy', 'true');

    track('mm_upsell_clicked', { surface, source: button.dataset.upsellSource, variantId: button.dataset.variantId, title: button.dataset.productTitle });

    try {
      const sectionId = cartSectionId(button);
      const response = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(button.dataset.variantId),
              quantity: 1,
              properties: {
                _upsell: button.dataset.productTitle,
                _upsell_source: button.dataset.upsellSource,
                _upsell_surface: surface,
              },
            },
          ],
          sections: sectionId || undefined,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        console.error('[mm-upsell] add failed', response.status);
        button.removeAttribute('disabled');
        button.removeAttribute('aria-busy');
        return;
      }

      const payload = await response.json().catch(() => ({}));
      const markup = sectionId && payload.sections && payload.sections[sectionId];

      if (markup) {
        const fresh = new DOMParser().parseFromString(markup, 'text/html').querySelector('cart-drawer, #cart-drawer');
        const live = document.querySelector('cart-drawer, #cart-drawer');
        if (fresh && live) live.innerHTML = fresh.innerHTML;
      } else if (surface === 'cart') {
        button.closest('.mmx-upsell__card')?.remove();
      }

      track('mm_upsell_added', { surface, source: button.dataset.upsellSource, variantId: button.dataset.variantId, title: button.dataset.productTitle });

      const cart = await fetch(CART_URL, { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null));
      if (cart) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart } }));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      if (surface !== 'cart' && button.isConnected) {
        button.textContent = '✓';
        setTimeout(() => {
          button.textContent = label;
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        }, 1600);
      }

      scan();
      openCartDrawer();
    } catch (error) {
      console.error('[mm-upsell] add threw', error);
      button.textContent = label;
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mmx-add]');
    if (!button) return;
    event.preventDefault();
    addToCart(button);
  });

  ['cart:change', 'cart:refresh', 'cart:updated', 'dialog:after-show'].forEach((name) => {
    document.addEventListener(name, () => setTimeout(scan, 60));
  });

  scan();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  }
})();